import { useState } from 'react'
import { Card, CardContent, CardHeader } from '@/app/common/components/Card.tsx'
import { Button } from '@/app/common/components/button/Button'
import { CircleLoader } from '@/app/common/components/CircleLoader'
import SimpleSelector from '@/app/common/components/SimpleSelector'
import { ChevronDownIcon } from '@/app/common/icons/arrows'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/shad/ui/toast/use-toast'
import { BankAccountQueries } from '@/queries/bank_accounts/queries'
import { BankAccount } from '@/queries/bank_accounts/schema'
import { useSetPurseBankAccount } from '@/queries/program_purses/useSetPurseBankAccount'
import { PURSE_KEYS } from '@/queries/program_purses/keys'
import { PartialPurseData } from '../types'

type Props = {
  partialPurseData: PartialPurseData
}

export default function PurseBankAccountCard({ partialPurseData }: Props) {
  const [isEditing, setIsEditing] = useState(false)
  const [selectedBankAccount, setSelectedBankAccount] =
    useState<BankAccount | null>(null)
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const mutation = useSetPurseBankAccount()

  const bankAccountsQuery = useQuery(
    BankAccountQueries.getPaginatedListByProgramId(
      100,
      1,
      partialPurseData.programId
    )
  )

  const bankAccounts = bankAccountsQuery.data?.data ?? []

  const hasExistingAccount = partialPurseData.bankAccountId != null
  const currentBankAccount = hasExistingAccount
    ? (bankAccounts.find((ba) => ba.id === partialPurseData.bankAccountId) ??
      null)
    : null

  const bankAccountOptions = bankAccounts
    .filter((ba) => ba.id !== partialPurseData.bankAccountId)
    .map((ba) => ({ id: ba.id, name: ba.name }))

  const handleAssign = () => {
    if (!selectedBankAccount) return
    mutation.mutate(
      {
        params: { program_purse_id: partialPurseData.id },
        payload: {
          program_id: partialPurseData.programId,
          bank_account_id: selectedBankAccount.id,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: PURSE_KEYS.PURSE_DETAILS(
              partialPurseData.id.toString(),
              partialPurseData.programId
            ),
          })
          setIsEditing(false)
          setSelectedBankAccount(null)
          toast({
            title: 'Success',
            description: 'Bank account assigned successfully',
          })
        },
        onError: (error) => {
          toast({
            title: 'Error',
            description: error.message,
            variant: 'destructive',
          })
        },
      }
    )
  }

  const isLoading = bankAccountsQuery.status === 'pending'
  const isEmpty = !isLoading && bankAccountOptions.length === 0
  const showSelector = !hasExistingAccount || isEditing

  return (
    <Card>
      <CardHeader>
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">
            Bank Account
          </h3>
          <p className="mt-1 text-sm text-gray-700">
            Select the bank account to be used for this purse's distributions.
          </p>
        </div>
      </CardHeader>
      <CardContent>
        {hasExistingAccount && (
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-sm text-gray-700">
              Selected Bank Account:{' '}
              <span className="font-bold text-gray-900">
                {currentBankAccount?.name ??
                  `Account #${partialPurseData.bankAccountId}`}
              </span>
            </p>
            {!isEditing && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )}
          </div>
        )}

        {showSelector && (
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <SimpleSelector
                list={bankAccountOptions}
                selected={
                  selectedBankAccount
                    ? (bankAccountOptions.find(
                        (o) => o.id === selectedBankAccount.id
                      ) ?? null)
                    : null
                }
                setSelected={(opt) => {
                  const ba = bankAccounts.find((b) => b.id === opt.id) ?? null
                  setSelectedBankAccount(ba)
                }}
                icon={<ChevronDownIcon className="text-lg" />}
                placeholder={
                  isLoading
                    ? 'Loading bank accounts...'
                    : isEmpty
                      ? 'No bank accounts available'
                      : 'Select a bank account'
                }
                isDisabled={isLoading || isEmpty}
              />
            </div>
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={handleAssign}
              disabled={!selectedBankAccount || mutation.status === 'pending'}
            >
              {mutation.status === 'pending' ? (
                <span className="flex items-center gap-1">
                  <CircleLoader size="sm" />
                  Assigning...
                </span>
              ) : (
                'Assign'
              )}
            </Button>
            {hasExistingAccount && isEditing && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsEditing(false)
                  setSelectedBankAccount(null)
                }}
              >
                Cancel
              </Button>
            )}
          </div>
        )}

        {bankAccountsQuery.status === 'error' && (
          <p className="mt-2 text-sm text-red-600">
            {bankAccountsQuery.error.message}
          </p>
        )}
      </CardContent>
    </Card>
  )
}
